import {
  format,
  getDaysInMonth,
  startOfMonth,
  getDay,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  addDays,
  parseISO,
  isSameDay,
  isToday as isTodayDateFns,
  isSameMonth,
  isSameYear,
} from 'date-fns';

/**
 * Get the number of days in a month
 * @param {Date} date - The date to check
 * @returns {number} Number of days in the month
 */
export const getDaysInMonthHelper = (date) => {
  return getDaysInMonth(date);
};

/**
 * Get the day of the week for the first day of the month (0-6, Sunday-Saturday)
 * @param {Date} date - The date to check
 * @returns {number} Day of the week (0-6)
 */
export const getFirstDayOfMonth = (date) => {
  return getDay(startOfMonth(date));
};

/**
 * Get formatted month name and year
 * @param {Date} date - The date to format
 * @returns {string} Formatted month and year (e.g., "January 2024")
 */
export const getMonthName = (date) => {
  return format(date, 'MMMM yyyy');
};

/**
 * Navigate to next/previous month
 * @param {Date} currentDate - Current date
 * @param {number} direction - Direction to navigate (1 for next, -1 for previous)
 * @returns {Date} New date after navigation
 */
export const navigateMonth = (currentDate, direction) => {
  return addMonths(currentDate, direction);
};

/**
 * Navigate to next/previous week
 * @param {Date} currentWeekStart - Current week start date
 * @param {number} direction - Direction to navigate (1 for next, -1 for previous)
 * @returns {Date} New date after navigation
 */
export const navigateWeek = (currentWeekStart, direction) => {
  return addWeeks(currentWeekStart, direction);
};

/**
 * Get all days in a week starting from a given date
 * @param {Date} startDate - Start date of the week
 * @returns {Date[]} Array of dates for the week
 */
export const getWeekDays = (startDate) => {
  return eachDayOfInterval({
    start: startDate,
    end: endOfWeek(startDate, { weekStartsOn: 0 }),
  });
};

/**
 * Get formatted week range string
 * @param {Date} startDate - Start date of the week
 * @returns {string} Formatted week range (e.g., "Jan 1-7, 2024" or "Dec 30 - Jan 5, 2024")
 */
export const getWeekRange = (startDate) => {
  const endDate = addDays(startDate, 6);
  const startMonth = format(startDate, 'MMM');
  const endMonth = format(endDate, 'MMM');
  const startDay = format(startDate, 'd');
  const endDay = format(endDate, 'd');
  const year = format(startDate, 'yyyy');

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`;
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }
};

/**
 * Filter posts for a specific date
 * @param {Array} posts - Array of post objects
 * @param {Date} date - Date to filter by
 * @returns {Array} Filtered posts for the date
 */
export const getPostsForDate = (posts, date) => {
  return posts.filter((post) => {
    if (!post.scheduledAt) return false;
    if (post.status === 'draft') return false;
    const postDate = parseISO(post.scheduledAt);
    return isSameDay(postDate, date);
  });
};

/**
 * Check if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  return isTodayDateFns(date);
};

/**
 * Check if a date is in the current month
 * @param {Date} date - Date to check
 * @param {Date} currentDate - Current date to compare against
 * @returns {boolean} True if date is in the same month and year
 */
export const isCurrentMonth = (date, currentDate) => {
  return isSameMonth(date, currentDate) && isSameYear(date, currentDate);
};

export const formatDateHelper = (date, dateFormat) => {
  return format(parseISO(date), dateFormat);
};
